import React, { FC, useState, useCallback } from "react";
import { Box, Stack, Typography, styled } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { IconFileImport } from "@/assets/icons";
import {
  useRecentOfferAccepted,
  exportTalentCsvToVisaApi,
  exportCompanyCsvToVisaApi,
  exportTalentCsvToVisaBulkApi,
  exportCompanyCsvToVisaBulkApi,
} from "@/api/services/main/dashboard";
import { TRecentOfferAcceptedItem } from "@/api/services/main/dashboard/dtos/recent-offer-accepted.dto";
import { CaseMatchStatusOptions, EXPIRY_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { background, button, divider, text, state } from "@/theme/colors";
import { CsvExportModal } from "@/features/dashboard/components/CsvExportModal";
import { useTableSelection } from "@/features/dashboard/hooks/useTableSelection";
import { truncateWithEllipsis } from "@/utils/string";
import { showError } from "@/utils/error";
import { CaseMatchStatusTag } from "@/features/talent/components/talent-common/CaseMatchStatusTag";
import { findLabelByValue } from "@/utils/object";
import { EMPTY_STR } from "@/constants/string";
import { TalentAvatarCell } from "@/components/commons/TalentAvatarCell";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { hasClassificationMismatch, hasCompanyClassificationMismatch, getExpiryStatus } from "@/features/case/utils/caseMatchHelper";
import { DATE_FORMAT } from "@/constants/datetime";
import { saveAs } from "file-saver";
import {
  CLASSIFICATION_MISMATCH_MESSAGE,
  RESIDENCE_EXPIRING_SOON_MESSAGE,
  RESIDENCE_EXPIRED_MESSAGE,
  PASSPORT_EXPIRING_SOON_MESSAGE,
  PASSPORT_EXPIRED_MESSAGE,
} from "@/features/case/utils/messageWarning";

const MAX_DISPLAY_ITEMS = 5;
const FETCH_LIMIT = 100;
const MAX_TEXT_LENGTH = 14;

const formatDate = (date: string | null | undefined): string => {
  if (!date) return EMPTY_STR.TEXT;
  return dayjs(date).format(DATE_FORMAT);
};

export const DashboardRecentOfferAccepted: FC = () => {
  const navigate = useNavigate();
  const { data: result, isLoading } = useRecentOfferAccepted(FETCH_LIMIT);
  const items = result?.data?.items ?? [];
  const totalCount = result?.data?.totalCount ?? 0;
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvModalMode, setCsvModalMode] = useState<"single" | "bulk">("single");
  const [csvTargetItem, setCsvTargetItem] = useState<TRecentOfferAcceptedItem | null>(null);
  const selection = useTableSelection();

  const displayItems = items.slice(0, MAX_DISPLAY_ITEMS);
  const hasMore = items.length > MAX_DISPLAY_ITEMS;
  const displayItemIds = displayItems.map((item) => item.id);
  const selectedItems = displayItems.filter((item) => selection.isSelected(item.id));

  const showSingleClassificationWarning = csvTargetItem
    ? hasClassificationMismatch(csvTargetItem.talent?.specificBusinessClassificationIds, csvTargetItem.case?.job?.companySpecificBusinessClassificationIds)
    : false;
  const showSingleCompanyClassificationWarning = csvTargetItem
    ? hasCompanyClassificationMismatch(
        csvTargetItem.case?.job?.company?.industryClassification?.companySpecificIndustryId1,
        csvTargetItem.case?.job?.company?.industryClassification?.companySpecificBusinessClassificationIds1,
        csvTargetItem.case?.job?.companySpecificIndustryId,
        csvTargetItem.case?.job?.companySpecificBusinessClassificationIds,
      )
    : false;
  const showBulkClassificationWarning = selectedItems.some((item) =>
    hasClassificationMismatch(item.talent?.specificBusinessClassificationIds, item.case?.job?.companySpecificBusinessClassificationIds),
  );
  const showBulkCompanyClassificationWarning = selectedItems.some((item) =>
    hasCompanyClassificationMismatch(
      item.case?.job?.company?.industryClassification?.companySpecificIndustryId1,
      item.case?.job?.company?.industryClassification?.companySpecificBusinessClassificationIds1,
      item.case?.job?.companySpecificIndustryId,
      item.case?.job?.companySpecificBusinessClassificationIds,
    ),
  );

  const handleCsvClick = useCallback((e: React.MouseEvent, item: TRecentOfferAcceptedItem) => {
    e.stopPropagation();
    setCsvTargetItem(item);
    setCsvModalMode("single");
    setCsvModalOpen(true);
  }, []);

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleBulkCsvClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCsvModalMode("bulk");
    setCsvModalOpen(true);
  }, []);

  const handleRowClick = useCallback((item: TRecentOfferAcceptedItem) => {
    if (!item.case?.job?.id || !item.case?.id) return;
    window.open(AppRoutes.caseEdit.replace(":jobId", String(item.case.job.id)).replace(":caseId", String(item.case.id)), "_blank");
  }, []);

  const handleExportCompany = useCallback(async () => {
    try {
      if (csvModalMode === "bulk") {
        const { blob, fileName } = await exportCompanyCsvToVisaBulkApi({
          isAll: false,
          caseMatchIds: selectedItems.map((item) => item.id),
        });
        saveAs(blob, fileName);
        return;
      }
      if (!csvTargetItem) return;
      await exportCompanyCsvToVisaApi(csvTargetItem.id, csvTargetItem.case?.job?.company?.companyName);
    } catch (e) {
      showError(e);
    }
  }, [csvModalMode, selectedItems, csvTargetItem]);

  const handleExportTalent = useCallback(async () => {
    try {
      if (csvModalMode === "bulk") {
        const { blob, fileName } = await exportTalentCsvToVisaBulkApi({
          isAll: false,
          caseMatchIds: selectedItems.map((item) => item.id),
        });
        saveAs(blob, fileName);
        return;
      }
      if (!csvTargetItem) return;
      await exportTalentCsvToVisaApi(csvTargetItem.id, csvTargetItem.talent?.fullName);
    } catch (e) {
      showError(e);
    }
  }, [csvModalMode, selectedItems, csvTargetItem]);

  return (
    <>
      <InnerBox
        title={totalCount > 0 ? `最近の内定受諾者（${totalCount}名）` : "最近の内定受諾者"}
        variantTitle="head20Bold"
        showDivider={false}
        loading={isLoading}
      >
        <Stack mt={2}>
          <TableContainer>
            <TableHeader>
              <HeaderCell sx={{ width: "40px" }}>
                <Checkbox
                  nonControl
                  checked={selection.isAllSelected(displayItemIds)}
                  onClick={stopPropagation}
                  onChange={() => selection.toggleAll(displayItemIds)}
                />
              </HeaderCell>
              <HeaderCell sx={{ width: "116px" }}>ステータス</HeaderCell>
              <HeaderCell sx={{ flex: 1 }}>氏名</HeaderCell>
              <HeaderCell sx={{ flex: 1 }}>呼び名</HeaderCell>
              <HeaderCell sx={{ flex: 1 }}>内定先企業名</HeaderCell>
              <HeaderCell sx={{ flex: 1 }}>求人名</HeaderCell>
              <HeaderCell sx={{ width: "110px" }}>内定受諾日</HeaderCell>
              <HeaderCell sx={{ width: "106px" }}>入社日</HeaderCell>
              <HeaderCell sx={{ width: "146px" }}>データ移行</HeaderCell>
            </TableHeader>

            {displayItems.length === 0 && !isLoading ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body14" color={text.tertiary} fontWeight={700}>
                  データがありません。
                </Typography>
              </Box>
            ) : (
              displayItems.map((item, index) => {
                const isLastItem = index === displayItems.length - 1;
                const showMismatchWarning = hasClassificationMismatch(
                  item.talent?.specificBusinessClassificationIds,
                  item.case?.job?.companySpecificBusinessClassificationIds,
                );
                const residenceExpiry = getExpiryStatus(item.talent?.periodOfStay);
                const passportExpiry = getExpiryStatus(item.talent?.passportExpiryDate);

                return (
                  <RowWrapper
                    key={item.id}
                    $isLast={isLastItem}
                    onClick={() => handleRowClick(item)}
                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: background.primary } }}
                  >
                    <TableRow>
                      <RowCell sx={{ width: "40px" }}>
                        <Checkbox
                          nonControl
                          checked={selection.isSelected(item.id)}
                          onClick={stopPropagation}
                          onChange={() => selection.toggle(item.id)}
                        />
                      </RowCell>
                      <RowCell sx={{ width: "116px" }}>
                        <CaseMatchStatusTag status={item.status} title={findLabelByValue(CaseMatchStatusOptions, item.status)} variant="bold" />
                      </RowCell>
                      <RowCell sx={{ flex: 1, gap: 1 }}>
                        <TalentAvatarCell filePath={item.talent?.avatarThumbnailPath} name={item.talent?.fullName} size={28} />
                        {item.talent?.fullName ? truncateWithEllipsis(item.talent.fullName, 20) : EMPTY_STR.TEXT}
                      </RowCell>
                      <RowCell sx={{ flex: 1 }}>
                        {item.talent?.nickName ? truncateWithEllipsis(item.talent.nickName, MAX_TEXT_LENGTH) : EMPTY_STR.TEXT}
                      </RowCell>
                      <RowCell sx={{ flex: 1 }}>
                        {item.case?.job?.company?.companyName
                          ? truncateWithEllipsis(item.case.job.company.companyName, MAX_TEXT_LENGTH)
                          : EMPTY_STR.TEXT}
                      </RowCell>
                      <RowCell sx={{ flex: 1 }}>
                        {item.case?.job?.jobName ? truncateWithEllipsis(item.case.job.jobName, MAX_TEXT_LENGTH) : EMPTY_STR.TEXT}
                      </RowCell>
                      <RowCell sx={{ width: "110px" }}>{formatDate(item.offerAcceptDate)}</RowCell>
                      <RowCell sx={{ width: "106px" }}>{formatDate(item.companyJoiningDate)}</RowCell>
                      <RowCell sx={{ width: "146px" }}>
                        <Button
                          variant="outline"
                          size="sm"
                          startIcon={<IconFileImport color={button.primary} width={20} height={20} />}
                          onClick={(e) => handleCsvClick(e, item)}
                        >
                          CSV取得
                        </Button>
                      </RowCell>
                    </TableRow>
                    {residenceExpiry && (
                      <WarningRow>
                        {residenceExpiry === EXPIRY_STATUS.EXPIRED ? RESIDENCE_EXPIRED_MESSAGE : RESIDENCE_EXPIRING_SOON_MESSAGE}
                      </WarningRow>
                    )}
                    {passportExpiry && (
                      <WarningRow>
                        {passportExpiry === EXPIRY_STATUS.EXPIRED ? PASSPORT_EXPIRED_MESSAGE : PASSPORT_EXPIRING_SOON_MESSAGE}
                      </WarningRow>
                    )}
                    {showMismatchWarning && <WarningRow>{CLASSIFICATION_MISMATCH_MESSAGE}</WarningRow>}
                  </RowWrapper>
                );
              })
            )}
          </TableContainer>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
          <Button variant="outline" size="sm" disabled={selection.includedIds.size === 0} onClick={handleBulkCsvClick}>
            チェックしたCSVをダウンロード
          </Button>
          {hasMore && (
            <Button variant="outline" size="sm" onClick={() => navigate(AppRoutes.offerAcceptedList)}>
              内定受諾者一覧を見る
            </Button>
          )}
        </Stack>
      </InnerBox>

      <CsvExportModal
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        showClassificationWarning={csvModalMode === "bulk" ? showBulkClassificationWarning : showSingleClassificationWarning}
        showCompanyClassificationWarning={csvModalMode === "bulk" ? showBulkCompanyClassificationWarning : showSingleCompanyClassificationWarning}
        onExportCompany={handleExportCompany}
        onExportTalent={handleExportTalent}
      />
    </>
  );
};

// Styled components
const TableContainer = styled(Box)`
  border: 1px solid ${divider.middle};
  border-radius: 8px;
  overflow: hidden;

  .MuiCheckbox-root {
    width: 24px !important;
    height: 24px !important;
    padding: 0 !important;
  }
`;

const TableHeader = styled(Stack)`
  flex-direction: row;
  align-items: center;
  background-color: ${background.primary};
  border-bottom: 1px solid ${divider.middle};
  padding: 10px 16px;
  gap: 8px;
`;

const HeaderCell = styled((props: React.ComponentProps<typeof Typography>) => <Typography variant="body14Bold" {...props} />)`
  color: ${text.primary};
  white-space: nowrap;
`;

const RowWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isLast",
})<{ $isLast: boolean }>`
  border-bottom: ${(props) => (props.$isLast ? "none" : `1px solid ${divider.middle}`)};
`;

const TableRow = styled(Stack)`
  flex-direction: row;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
`;

const RowCell = styled(Box)`
  font-size: 14px;
  font-weight: 400;
  color: ${text.primary};
  display: flex;
  align-items: center;
`;

const WarningRow = styled(Box)`
  padding: 0 16px 8px;
  font-size: 12px;
  color: ${state.warning_1};
`;
